import styles from 'css/src/components/common/Notifications.module.scss';

import React from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import type { SelectCallback } from '@restart/ui/types';

import {
    ProductUpdates,
    useProductUpdatesUnreadCount,
} from 'js/src/components/common/ProductUpdates';
import Avatar from 'js/src/components/common/ui/Avatar';
import {
    BUTTON_SHAPES,
    BUTTON_WIDTHS,
    SecondaryIconButton,
} from 'js/src/components/common/ui/Button';
import {
    ContentPlaceholderCircle,
    ContentPlaceholderSingleLineText,
} from 'js/src/components/common/ui/ContentPlaceholder';
import Drawer from 'js/src/components/common/ui/Drawer';
import { Tab, Tabs } from 'js/src/components/common/ui/Tabs';
import TimeAgo from 'js/src/components/common/ui/TimeAgo';
import {
    getHasUnreadNotifications,
    loadNotifications,
    markAllNotificationsAsRead,
    markNotificationAsRead,
    NotificationUiState,
    selectAllNotificationIds,
    selectHasUnreadNotifications,
    selectNotificationById,
    selectNotificationsUiState,
} from 'js/src/data/notifications';
import { useOnceBrowserIdleEffect } from 'js/src/hooks/useOnceBrowserIdleEffect';
import { getUserInitials } from 'js/src/libs/custom-utils';
import type { RootState } from 'js/src/libs/RootState';
import { useAppDispatch } from 'js/src/libs/RootState';

enum TAB_KEYS {
    UPDATES = 'updates',
    ACTIVITY = 'activity',
}

const RedDot = ({ className }: { className?: string }) => (
    <div className={classNames(className, styles['red-dot'])} />
);

type NotificationMessageProps = {
    id: number;
};

/**
 * Renders a Notification component containing the recipient name, created_at time in <TimeAgo/> form,
 * the notification message, and the avatar of the user of the trigger of the notification.
 */
const NotificationMessage = React.memo(function Notification({ id }: NotificationMessageProps) {
    const dispatch = useAppDispatch();
    const notification = useSelector((state: RootState) => selectNotificationById(state, id));
    if (!notification) {
        throw new Error(`Notification with id ${id} not found`);
    }

    const handleNotificationClick = React.useCallback(() => {
        dispatch(markNotificationAsRead(notification.id)).then(() => {
            // tracking
            const url = notification.url;
            analytics.track(
                'In app notification clicked',
                {
                    type: notification.type,
                },
                () => {
                    if (url === '') return;
                    location.href = url;
                },
            );

            // if url is blank, we do nothing, so we should refresh the notifications
            dispatch(loadNotifications()).catch(() => {
                // server error: do nothing, but catch to avoid sending this to sentry
            });
        });
    }, [dispatch, notification]);

    return (
        <div className={styles['notification']} onClick={handleNotificationClick}>
            <div className="d-flex">
                <Avatar
                    className="flex-shrink-0" // preserve width
                    image={notification.photo_url}
                    text={getUserInitials(notification.photo_fallback)}
                    topLeftBadge={notification.read_status ? null : <RedDot />}
                />

                <div className="notification__text-block">
                    <p className="my-l5-auto ms-l5-16">{notification.content}</p>
                    <TimeAgo time={notification.created_at} className="ms-l5-16 mt-l5-4" muted />
                </div>
            </div>
        </div>
    );
});

type NotificationsProps = {
    notification_ids: readonly number[];
};

/**
 * Renders a list of <NotificationMessage/>
 */
const Notifications = React.memo(function Notifications({ notification_ids }: NotificationsProps) {
    return (
        <div className={styles['notification-list']}>
            {notification_ids.map((notificationId) => {
                return (
                    <NotificationMessage
                        key={`${notificationId}-notification`}
                        id={notificationId}
                    />
                );
            })}
        </div>
    );
});

type NotificationDrawerToggleProps = {
    className?: string;
    onClick: VoidFunction;
};

/**
 * Toggle to open the notification drawer. Renders a bell icon and on render calls /api/has-unread-notifications
 */
export const NotificationDrawerToggle = React.memo(function NotificationDrawerToggle({
    className = '',
    onClick,
}: NotificationDrawerToggleProps) {
    const dispatch = useAppDispatch();
    const hasUnread = useSelector(selectHasUnreadNotifications);
    const numUpdates = useProductUpdatesUnreadCount();

    // Cosmetic (red dot on the bell) — defer off the dashboard's critical
    // render path.
    useOnceBrowserIdleEffect(() => {
        dispatch(getHasUnreadNotifications()).catch(() => {
            // server error: do nothing, but catch to avoid sending this to sentry
        });
    }, [dispatch]);

    const onClickCallback = React.useCallback(() => {
        onClick(); // calling onClick without any parameters triggers toggle behaviour
    }, [onClick]);

    return (
        <div className={classNames(className, styles['notification__drawer-toggle'])}>
            {(hasUnread || numUpdates > 0) && <RedDot />}
            <SecondaryIconButton icon="notifications_none" onClick={onClickCallback} />
        </div>
    );
});

type NotificationsActionsProps = {
    className?: string;
};

/**
 * Actions within the notifications, like "refresh" and "mark all as read"
 */
const NotificationsActions = React.memo(function NotificationsActions({
    className = '',
}: NotificationsActionsProps) {
    const dispatch = useAppDispatch();

    const handleRefresh = React.useCallback(() => {
        dispatch(loadNotifications()).catch(() => {
            // server error: do nothing, but catch to avoid sending this to sentry
        });
    }, [dispatch]);

    const handleMarkAllAsRead = React.useCallback(() => {
        dispatch(markAllNotificationsAsRead()).then(() => {
            dispatch(loadNotifications()).catch(() => {
                // server error: do nothing, but catch to avoid sending this to sentry
            });
        });
    }, [dispatch]);

    return (
        <div className={classNames(className, 'd-flex', styles['notification__actions'])}>
            <SecondaryIconButton
                onClick={handleRefresh}
                icon="sync"
                tooltipTitle="Refresh notifications"
                width={BUTTON_WIDTHS.NO_MIN_WIDTH}
                shape={BUTTON_SHAPES.CIRCLE}
            />
            <SecondaryIconButton
                className="mark-all-as-read"
                onClick={handleMarkAllAsRead}
                icon="check_circle"
                tooltipTitle="Mark all as read"
                width={BUTTON_WIDTHS.NO_MIN_WIDTH}
                shape={BUTTON_SHAPES.CIRCLE}
            />
        </div>
    );
});

const EmptyNotifications = () => (
    <div className="d-flex flex-column">
        <img
            className="mt-l5-48 d-flex align-self-center"
            width={165}
            src="https://cdn.lumen5.com/lumen5-site-images/empty-notifications.svg"
        />
        <h6 className="text-center">Your notifications live here.</h6>
    </div>
);
const LoadingNotifications = () => (
    <>
        {[...Array(10)].map((_, key) => (
            <div key={key} className={classNames(styles['notification'], 'd-flex')}>
                <ContentPlaceholderCircle styleProps={{ minWidth: '38px', height: '38px' }} />
                <div className="w-100">
                    <ContentPlaceholderSingleLineText className="ms-l5-16 mb-l5-8" />
                    <ContentPlaceholderSingleLineText
                        className="ms-l5-16"
                        styleProps={{ width: '50%' }}
                    />
                </div>
            </div>
        ))}
    </>
);

type NotificationDrawerProps = {
    show: boolean;
    onHide?: VoidFunction;
};

/**
 * In-app notification centre
 */
const NotificationDrawer = React.memo(function NotificationDrawer({
    show,
    onHide,
}: NotificationDrawerProps) {
    const dispatch = useAppDispatch();
    const notification_ids = useSelector(selectAllNotificationIds);
    const uiState = useSelector(selectNotificationsUiState);
    const hasUnread = useSelector(selectHasUnreadNotifications);
    const numUpdates = useProductUpdatesUnreadCount();

    const [tabKey, setTabKey] = React.useState(TAB_KEYS.ACTIVITY);

    const handleDrawerOpen = React.useCallback(() => {
        dispatch(loadNotifications()).catch(() => {
            // server error: do nothing, but catch to avoid sending this to sentry
        });

        analytics.track('In app notification drawer opened');
    }, [dispatch]);

    const onTabSelect = React.useCallback(
        (eventKey: TAB_KEYS, e: React.SyntheticEvent<unknown>) => {
            setTabKey(eventKey);
        },
        [],
    ) as SelectCallback;

    let content = <EmptyNotifications />;
    switch (uiState) {
        case NotificationUiState.LOADING: {
            content = <LoadingNotifications />;
            break;
        }
        case NotificationUiState.EMPTY: {
            content = <EmptyNotifications />;
            break;
        }
        case NotificationUiState.SHOWING: {
            content = <Notifications notification_ids={notification_ids} />;
            break;
        }
        default: {
            content = <EmptyNotifications />;
        }
    }

    return (
        <Drawer
            drawerClassName={classNames(
                'navbar-offset notification-drawer',
                styles['notification-drawer'],
            )} // adds a margin top exactly the height of the navbar
            backdropClassName="navbar-offset" // adds a margin top exactly the height of the navbar
            headerClassName={styles['notification-drawer__header']}
            bodyClassName={styles['notification-drawer__body']}
            show={show}
            onShow={handleDrawerOpen}
            onHide={onHide}
            title="Notifications"
        >
            <Tabs activeKey={tabKey} onSelect={onTabSelect}>
                <Tab
                    eventKey={TAB_KEYS.ACTIVITY}
                    title={
                        <span>
                            {hasUnread && <RedDot className="me-l5-8" />}
                            Activity
                        </span>
                    }
                >
                    <NotificationsActions className="justify-content-end mx-l5-16" />
                    {content}
                </Tab>
                <Tab
                    eventKey={TAB_KEYS.UPDATES}
                    title={
                        <span>
                            {numUpdates > 0 && <RedDot className="me-l5-8" />}
                            Updates
                        </span>
                    }
                    className="product-updates-panel"
                >
                    <ProductUpdates />
                </Tab>
            </Tabs>
        </Drawer>
    );
});

export default NotificationDrawer;
